/**
 * Credit buckets API wrapper (v2 — Project Independence, DNKADM-0052 U2).
 *
 * The credit bucket is the single most important v2 model change (handoff §5.4):
 * a **first-class object** `v2_credit_bucket` with its own id. Credits, daily caps,
 * cancellation bands, membership-plan credit deposits, and the credit ledger all
 * key off **`creditBucketId`** (not a category string as in v1). A service points
 * to exactly one bucket (1:1, §5.5). This module owns the CRUD +
 * soft-delete/reactivate (§4.3) and the canonical fail-closed
 * **{@link useCreditBucketPickList}** every downstream feature imports.
 *
 * **White-label (§4.0):** `ghlCustomFieldKey` is the explicit contact-system
 * mirror target for this bucket's balance — it is **DATA**, never a rendered brand.
 * The field is carried/submitted as-is here; its INPUT is labeled brand-neutral in
 * the dialog via `CONTACT_SYSTEM_FIELD_KEY_LABEL` (`src/utils/whitelabel.ts`).
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded
 * 401→refresh→retry; the mutating-audit `X-Session-Mode` stamp fires on the writes.
 *
 * **Endpoints (handoff §5.4, §3.1 — standard REST + soft-delete):**
 *  - `GET    /v2/admin/credit-buckets?status=active|inactive|all` → {@link CreditBucket}`[]`.
 *  - `POST   /v2/admin/credit-buckets` ← {@link CreditBucketInput} → the created row.
 *  - `PUT    /v2/admin/credit-buckets/{id}` ← {@link CreditBucketInput} → the updated row.
 *  - `POST   /v2/admin/credit-buckets/{id}/deactivate` | `/reactivate` (§4.3) → the updated row.
 *
 * **`v2_credit_bucket` field set (handoff §5.4):** `id`, `name`, `description`
 * (optional), `ghlCustomFieldKey` (optional — the mirror target, decoupled from the
 * name), `isActive`, `pricePerCredit` (nullable — DNKADM-0237; `null` = unpriced /
 * not sellable, non-null must be `> 0`), plus the read-only sellable fields the
 * sell cart needs — `maxPurchaseQuantity`, `soldCreditExpiryDays`, `taxable`
 * (DNKADM-0241; not settable through {@link CreditBucketInput}). Unique on
 * `(tenant, name)` — the create/update `409` surfaces inline. Every field is
 * parsed DEFENSIVELY (mirrors `locations.ts`).
 *
 * **`unit` (DNKADM-0319 / DNKAPI-0484):** the tenant-chosen noun a member reads the
 * quantity in — `hours` / `sessions` / `credits`, lowercase on the wire; `credits`
 * is the server default and DB backfill. **Update semantic differs from every other
 * field:** on PUT an absent/blank `unit` means *unchanged* (not reset), so the
 * dialog always sends an explicit value on update.
 */
import { request } from './client';
import { useCallback } from 'react';
import { usePickList, type PickList } from '@/hooks/usePickList';
import { PickListError } from './picklists';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';

/** v2 admin credit-buckets base path (bearer surface). */
const PATH = '/v2/admin/credit-buckets';

/**
 * The closed set of credit units (dink-api `CreditUnit`, DNKAPI-0484), as the
 * LOWERCASE wire values. Order is the dialog's option order.
 */
export const CREDIT_UNITS = ['hours', 'sessions', 'credits'] as const;

/** How a member reads a bucket's quantity: "1.5 hours" / "2 sessions" / "3 credits". */
export type CreditUnit = (typeof CREDIT_UNITS)[number];

/** The server default (and the DB backfill for never-configured buckets). */
export const DEFAULT_CREDIT_UNIT: CreditUnit = 'credits';

/** Operator-facing label for a unit (the dialog's option text and the list's marker). */
export function creditUnitLabel(unit: CreditUnit): string {
  switch (unit) {
    case 'hours':
      return 'Hours';
    case 'sessions':
      return 'Sessions';
    case 'credits':
      return 'Credits';
  }
}

/**
 * One `v2_credit_bucket` row — a first-class credit container (§5.4). `id` is the
 * `creditBucketId` everything downstream keys off. `description` and
 * `ghlCustomFieldKey` are optional strings (absent when the server carries none —
 * `exactOptionalPropertyTypes`: the key is omitted, never `undefined`).
 */
export interface CreditBucket {
  id: string;
  name: string;
  /** Soft-delete flag (§4.3); `false` = deactivated. */
  isActive: boolean;
  /**
   * Price per credit in the tenant currency (DNKADM-0237), or `null` when the
   * bucket is **unpriced / not sellable** — the member app's "Buy more" affordance
   * only appears for a bucket with a price `> 0`. Parsed defensively: anything
   * that is not a finite number `> 0` reads as `null` (the parser always assigns
   * the key, so consumers may simply check `!= null`).
   */
  pricePerCredit?: number | null;
  /**
   * Per-bucket purchase cap (DNKADM-0241); `null`/absent = uncapped. The sell
   * cart bounds its quantity stepper to it — the cap applies to the SUMMED
   * quantity of a bucket, so a cart never emits two lines for one bucket.
   */
  maxPurchaseQuantity?: number | null;
  /**
   * Days until sold credits expire (DNKADM-0241); `0` = never expires. `null`/
   * absent = the server said nothing, so no expiry hint is shown.
   */
  soldCreditExpiryDays?: number | null;
  /** Whether the bucket is taxable (DNKADM-0241). Absent when the server omits it. */
  taxable?: boolean;
  /**
   * The unit a member reads the quantity in (DNKADM-0319). Always assigned on
   * read: an absent or unrecognized wire value reads as the server default
   * `credits` (which is also what the server backfilled).
   */
  unit: CreditUnit;
  description?: string;
  /**
   * The explicit contact-system mirror target for this bucket's balance (§5.4) —
   * DATA, decoupled from the name. Rendered brand-neutral (§4.0): never as "GHL …".
   */
  ghlCustomFieldKey?: string;
}

/**
 * Create/update body for a credit bucket. `name` is required by the form;
 * `description` and `ghlCustomFieldKey` are optional. `isActive` is NOT settable
 * here — activation is the dedicated deactivate/reactivate endpoints (§4.3).
 */
export interface CreditBucketInput {
  name: string;
  /**
   * Price per credit (DNKADM-0237). `null` is the documented "unpriced / not
   * sellable" state and MUST be sent explicitly when the operator clears the
   * field — never `0`, never omitted. When non-null the server requires `> 0`
   * (and applies its own documented rounding — not duplicated client-side).
   */
  pricePerCredit?: number | null;
  /**
   * The member-facing unit (DNKADM-0319). On create, absent defaults to `credits`
   * server-side; on update, absent means **unchanged** (the one non-full-replace
   * field on this DTO), so the dialog sets it explicitly on every save.
   */
  unit?: CreditUnit;
  description?: string;
  ghlCustomFieldKey?: string;
}

/** Options for {@link listCreditBuckets}. */
export interface ListCreditBucketsOptions {
  /** Active/Inactive/All filter (§4.3); defaults to `active` (live rows only). */
  status?: ActiveFilterValue;
}

/**
 * The minimal `{ id, name }` shape the shared {@link useCreditBucketPickList}
 * selector returns — the canonical option every downstream bucket picker binds.
 */
export interface CreditBucketOption {
  id: string;
  name: string;
}

// --- Defensive parsing helpers (mirrors locations.ts) --------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI/router use — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null. Preserves
 * genuinely-string ids (accepts both); a numeric id is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/**
 * A boolean from a real boolean, or a fallback when absent/unparseable. `isActive`
 * defaults to `true` on read (a row the server returned without the flag is treated
 * as live — the list endpoint returns live rows by default, §4.3).
 */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * A REAL boolean, or null when the key is absent/unparseable — for optional flags
 * that carry no safe default (an absent `taxable` is "the server said nothing",
 * which is not the same claim as "not taxable").
 */
function asOptionalBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/** A finite number (number or numeric string), or null (mirrors `services.ts`). */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/**
 * A finite price `> 0`, or `null` — the documented "unpriced / not sellable"
 * state. `null`, absence, `0`, and a malformed value all read as unpriced.
 */
function asPositivePrice(value: unknown): number | null {
  const n = asNumber(value);
  return n !== null && n > 0 ? n : null;
}

/**
 * A {@link CreditUnit} from the wire (case-insensitive, like the server's
 * `fromWire`), or the `credits` default for anything absent/blank/unrecognized —
 * the same value the server backfilled, so a malformed row never throws.
 */
function asCreditUnit(value: unknown): CreditUnit {
  const text = asText(value)?.toLowerCase();
  const match = CREDIT_UNITS.find((u) => u === text);
  return match ?? DEFAULT_CREDIT_UNIT;
}

/**
 * Parse one raw bucket, or null when it has no usable `id` (an entry with no
 * selectable id is dropped rather than surfaced as a half-identified option — a
 * blank bucket id could otherwise slip through the downstream picker). A missing
 * `name` degrades to an empty string. `description`/`ghlCustomFieldKey` and the
 * DNKADM-0241 sellable fields (`maxPurchaseQuantity`, `soldCreditExpiryDays`,
 * `taxable`) are assigned only when present (exactOptionalPropertyTypes) — an
 * absent optional field reads as `undefined`, which the `!= null` checks the
 * consumers use treat exactly like the documented explicit `null`.
 */
function parseBucket(raw: unknown): CreditBucket | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;

  const bucket: CreditBucket = {
    id,
    name: asText(raw['name']) ?? '',
    // The wire flag is `active` (DNKADM-0064); tolerate legacy `isActive`.
    isActive: asBoolean(raw['active'] ?? raw['isActive'], true),
    pricePerCredit: asPositivePrice(raw['pricePerCredit']),
    unit: asCreditUnit(raw['unit']),
  };
  // DNKADM-0241 sellable fields. Both numbers are carried VERBATIM as the server
  // states them — `soldCreditExpiryDays: 0` is the meaningful "never expires", so
  // neither may go through the price helper (which reads 0 as absent).
  const maxPurchaseQuantity = asNumber(raw['maxPurchaseQuantity']);
  if (maxPurchaseQuantity !== null) bucket.maxPurchaseQuantity = maxPurchaseQuantity;
  const soldCreditExpiryDays = asNumber(raw['soldCreditExpiryDays']);
  if (soldCreditExpiryDays !== null) bucket.soldCreditExpiryDays = soldCreditExpiryDays;
  const taxable = asOptionalBoolean(raw['taxable']);
  if (taxable !== null) bucket.taxable = taxable;
  const description = asText(raw['description']);
  if (description !== null) bucket.description = description;
  // `ghlCustomFieldKey` is a genuine external mirror-target string — keep asText.
  const ghlCustomFieldKey = asText(raw['ghlCustomFieldKey']);
  if (ghlCustomFieldKey !== null) bucket.ghlCustomFieldKey = ghlCustomFieldKey;
  return bucket;
}

/**
 * Extract the buckets array TOLERANTLY from a successful body, returning `null`
 * when NEITHER a bare array NOR a known wrapper key
 * (`creditBuckets`/`buckets`/`content`) yields one. The list reads turn `null`
 * into `[]` (an empty list is a real first-run state, §8); the fail-closed
 * pick-list turns it into a {@link PickListError} (a non-array success cannot be
 * trusted as "complete and empty").
 */
function extractBuckets(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['creditBuckets'])) return body['creditBuckets'];
    if (Array.isArray(body['buckets'])) return body['buckets'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return null;
}

/** Build the JSON body for a create/update, omitting absent optional keys. */
function toBody(input: CreditBucketInput): Record<string, unknown> {
  const json: Record<string, unknown> = { name: input.name };
  // `null` is meaningful (unpriced / not sellable) and must be SENT, not omitted.
  if (input.pricePerCredit !== undefined) json['pricePerCredit'] = input.pricePerCredit;
  if (input.unit !== undefined) json['unit'] = input.unit;
  if (input.description !== undefined) json['description'] = input.description;
  if (input.ghlCustomFieldKey !== undefined) {
    json['ghlCustomFieldKey'] = input.ghlCustomFieldKey;
  }
  return json;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/credit-buckets?status=…` → the tenant's credit buckets,
 * defensively parsed (a bad entry is dropped). The `status` filter (§4.3) defaults
 * to `active` (live rows only); pass `inactive`/`all` to include deactivated rows.
 * A body that yields no array degrades to `[]` (a real first-run state, §8). Throws
 * a typed `ApiError` on a non-2xx response.
 */
export async function listCreditBuckets(
  options: ListCreditBucketsOptions = {},
): Promise<CreditBucket[]> {
  const status = options.status ?? 'active';
  const body = await request<unknown>('GET', PATH, { query: { status } });
  return (extractBuckets(body) ?? [])
    .map(parseBucket)
    .filter((b): b is CreditBucket => b !== null);
}

/**
 * `POST /v2/admin/credit-buckets` → create a bucket. Returns the re-parsed created
 * row. Throws a typed `ApiError` (`400` VALIDATION inline-able, `409` CONFLICT on
 * the unique `(tenant, name)` rule, upstream).
 */
export async function createCreditBucket(
  input: CreditBucketInput,
): Promise<CreditBucket | null> {
  const body = await request<unknown>('POST', PATH, { json: toBody(input) });
  return parseBucket(body);
}

/**
 * `PUT /v2/admin/credit-buckets/{id}` → update a bucket. Returns the re-parsed
 * updated row. Throws a typed `ApiError`.
 */
export async function updateCreditBucket(
  id: string,
  input: CreditBucketInput,
): Promise<CreditBucket | null> {
  const body = await request<unknown>('PUT', `${PATH}/${encodeURIComponent(id)}`, {
    json: toBody(input),
  });
  return parseBucket(body);
}

/**
 * `POST /v2/admin/credit-buckets/{id}/deactivate` → soft-deactivate a bucket (§4.3
 * — never a hard delete). Throws a typed `ApiError`.
 */
export async function deactivateCreditBucket(id: string): Promise<void> {
  await request<unknown>('POST', `${PATH}/${encodeURIComponent(id)}/deactivate`);
}

/**
 * `POST /v2/admin/credit-buckets/{id}/reactivate` → reactivate a soft-deactivated
 * bucket (§4.3). Throws a typed `ApiError`.
 */
export async function reactivateCreditBucket(id: string): Promise<void> {
  await request<unknown>('POST', `${PATH}/${encodeURIComponent(id)}/reactivate`);
}

// --- The shared, fail-closed credit-bucket pick-list ---------------------------

/**
 * Load the active credit buckets as `{ id, name }` options, **fail-closed**: any
 * upstream/network failure OR a malformed (non-array) `200` body throws the shared
 * {@link PickListError}, so the loader renders error + Retry and NEVER a silent
 * empty list (a failure can never masquerade as "loaded, empty"). A successful
 * empty list returns `[]` (a real first-run state — the caller renders "no
 * buckets"). Only `active` buckets are offered (a deactivated bucket must not be
 * selectable on a new service/cap/band).
 *
 * Mirrors the `@/api/picklists` fail-closed wrappers, reusing their `PickListError`
 * so `usePickList`'s `failClosed` detection (and the loader copy) work unchanged.
 */
export async function getCreditBucketOptions(): Promise<CreditBucketOption[]> {
  try {
    const body = await request<unknown>('GET', PATH, { query: { status: 'active' } });
    const list = extractBuckets(body);
    // A non-array success cannot be trusted as a COMPLETE list — fail-closed
    // rather than present it as an empty pick-list.
    if (list === null) throw new Error('expected an array of credit buckets');
    return list
      .map(parseBucket)
      .filter((b): b is CreditBucket => b !== null)
      .map((b) => ({ id: b.id, name: b.name }));
  } catch (cause) {
    throw new PickListError('ghl-credit-buckets', cause);
  }
}

/**
 * `useCreditBucketPickList()` — the **canonical** fail-closed credit-bucket
 * selector every downstream feature imports (services 0055, daily caps +
 * cancellation bands 0057, membership-plan credits 0056, member credits 0059).
 *
 * Loads live via {@link getCreditBucketOptions} through `usePickList`
 * (fail-closed): `loading → ready` with the `{ id, name }[]` options, or
 * `loading → error + Retry` — never a partial/empty-on-failure list. Render through
 * `<PickListLoader>` (`@/components/PickListLoader`). Name kept EXACTLY — downstream
 * specs import it verbatim.
 */
export function useCreditBucketPickList(): PickList<CreditBucketOption> {
  return usePickList(useCallback(() => getCreditBucketOptions(), []), []);
}
